/**
 * <QuickChargeDrawer> — canonical per AR-7. Uses posting templates only (D13).
 */
import { useState } from "react";
import { toast } from "sonner";
import { OperationalDrawer, BottomActionBar, QuickActionGrid } from "@/components/operational";
import {
  Shirt, Plane, Car, AlertOctagon, Wine, Bed, UtensilsCrossed, PackageOpen, Receipt,
} from "lucide-react";
import { foApi , formatApiError } from "../foApi";
import { NumberInput } from "@/components/ui/number-input";

const TEMPLATES = [
  { code: "MISC_LAUNDRY",         label: "Laundry",         icon: Shirt },
  { code: "MISC_AIRPORT_PICKUP",  label: "Airport Pickup",  icon: Plane },
  { code: "MISC_TAXI",            label: "Taxi",            icon: Car },
  { code: "MISC_DAMAGE",          label: "Damage",          icon: AlertOctagon },
  { code: "MISC_MINIBAR",         label: "Minibar",         icon: Wine },
  { code: "MISC_EXTRA_BED",       label: "Extra Bed",       icon: Bed },
  { code: "MISC_FOOD_ADJUST",     label: "Food Adjustment", icon: UtensilsCrossed },
  { code: "MISC_OTHER",           label: "Other",           icon: PackageOpen },
];

export default function QuickChargeDrawer({ open, onOpenChange, folio, stay, onSuccess }) {
  const [template, setTemplate] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!template) { toast.error("Pick a category"); return; }
    const minor = Math.round(parseFloat(amount) * 100);
    if (!minor || minor <= 0) { toast.error("Enter a positive amount"); return; }
    setSaving(true);
    try {
      await foApi.quickCharge({
        folio_id: folio.id, stay_id: stay?.id,
        template_code: template.code, amount_minor: minor, note,
      });
      setTemplate(null); setAmount(""); setNote("");
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e, "Charge failed"));
    } finally { setSaving(false); }
  };

  return (
    <OperationalDrawer open={open} onOpenChange={onOpenChange}
      title="Quick Charge" subtitle={folio?.folio_no || "Folio"}
      size="md" testid="quickcharge-drawer"
      footer={
        <BottomActionBar
          primary={{ label: saving ? "Posting..." : "Post Charge",
                       icon: Receipt, tone: "primary", onClick: submit,
                       loading: saving, disabled: !template || !amount,
                       testid: "quickcharge-confirm" }}
          secondaryActions={[
            { label: "Cancel", tone: "secondary", onClick: () => onOpenChange(false), testid: "quickcharge-cancel" }
          ]}
        />
      }>
      <div className="p-4 space-y-4">
        <div>
          <div className="text-[12px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Category</div>
          <QuickActionGrid
            columns={4}
            actions={TEMPLATES.map(t => ({
              id: t.code,
              label: t.label,
              icon: t.icon,
              tone: template?.code === t.code ? "active" : "ready",
              onClick: () => setTemplate(t),
              testid: `qc-tpl-${t.code}`,
            }))}
          />
        </div>
        <div>
          <label className="text-[12px] uppercase tracking-wider text-slate-500 font-semibold">Amount (₹)</label>
          <NumberInput min="0" step="0.01" value={amount}
                   onChange={e => setAmount(e.target.value)}
                   className="w-full mt-1 border-2 border-slate-300 rounded-lg p-3 text-[18px] font-semibold h-14"
                   placeholder="0.00"
                   data-testid="qc-amount"/>
        </div>
        <div>
          <label className="text-[12px] uppercase tracking-wider text-slate-500 font-semibold">Note (optional)</label>
          <textarea value={note} onChange={e => setNote(e.target.value)}
                      className="w-full mt-1 border-2 border-slate-300 rounded-lg p-2 text-[14px] min-h-[60px]"
                      data-testid="qc-note"/>
        </div>
      </div>
    </OperationalDrawer>
  );
}
